import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Plus, Sheet } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import { Modal } from "../components/ui/Modal";
import { dataSheetsApi, type DataSheetSummary } from "../api/dataSheetsApi";
import { HttpError } from "../api/http";
import { useCanEditSchema } from "../hooks/useCanEditSchema";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: DataSheetSummary[] }
  | { status: "error"; message: string };

/**
 * Landing page for the "Data Sheets" feature. Lists all tenant-owned
 * data sheets and — only for sessions that are allowed to edit the
 * schema (see {@link useCanEditSchema}) — exposes a primary "+ New data
 * sheet" action in the page header that opens a modal. Clicking a row
 * opens the records page for that sheet; the schema-editor is reachable
 * from a separate Schema button on each row.
 */
export function DataSheetsPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const canEditSchema = useCanEditSchema();

  const reload = async () => {
    setState({ status: "loading" });
    try {
      const response = await dataSheetsApi.list();
      setState({ status: "ready", items: response.items ?? [] });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load data sheets";
      setState({ status: "error", message });
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [] as DataSheetSummary[];
    const q = search.trim().toLowerCase();
    const sorted = state.items
      .slice()
      .sort((a, b) =>
        a.name.localeCompare(b.name, "en", { sensitivity: "base" }),
      );
    if (!q) return sorted;
    return sorted.filter((x) => {
      const hay = [x.name, x.description ?? ""].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [state, search]);

  const total = state.status === "ready" ? state.items.length : 0;

  const existingNames = useMemo(() => {
    if (state.status !== "ready") return new Set<string>();
    return new Set(state.items.map((x) => x.name.trim().toLowerCase()));
  }, [state]);

  const actions = canEditSchema ? (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => setModalOpen(true)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <Plus size={16} />
      New data sheet
    </button>
  ) : undefined;

  return (
    <>
      <PageHeader title="Data Sheets" actions={actions} />
      <div className="dashboard">
        {state.status === "loading" && <LoadingState />}
        {state.status === "error" && <ErrorState message={state.message} />}
        {state.status === "ready" && state.items.length === 0 && (
          <EmptyState
            canCreate={canEditSchema}
            onCreate={() => setModalOpen(true)}
          />
        )}
        {state.status === "ready" && state.items.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by name"
              count={{ matched: filtered.length, total }}
            />
            {filtered.length === 0 ? (
              <div className="tile" style={{ color: "var(--portal-muted)" }}>
                No data sheets match this search.
              </div>
            ) : (
              <SheetsRowList
                items={filtered}
                onOpenRecords={(item) => navigate(`/data/${item.sheetId}/records`)}
                onOpenSchema={(item) => navigate(`/data/${item.sheetId}`)}
                canEditSchema={canEditSchema}
              />
            )}
          </>
        )}
      </div>

      <CreateSheetModal
        open={modalOpen && canEditSchema}
        onClose={() => setModalOpen(false)}
        existingNames={existingNames}
        onCreated={(sheetId) => {
          setModalOpen(false);
          // Straight into the schema editor for the freshly-created sheet;
          // the user almost always wants to define columns next.
          navigate(`/data/${sheetId}`);
        }}
      />
    </>
  );
}

function CreateSheetModal({
  open,
  onClose,
  existingNames,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  existingNames: Set<string>;
  onCreated: (sheetId: number) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset internal state every time the modal transitions to closed so
  // the next open starts on a clean slate (empty name, no stale error).
  useEffect(() => {
    if (!open) {
      setName("");
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const trimmed = name.trim();
  const duplicate =
    trimmed.length > 0 && existingNames.has(trimmed.toLowerCase());
  const canSubmit = !busy && trimmed.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const created = await dataSheetsApi.create({
        name: trimmed,
        description: "",
        columns: [],
      });
      onCreated(created.sheetId);
    } catch (err) {
      const fallback =
        err instanceof Error ? err.message : "Failed to create data sheet";
      const apiDetail =
        err instanceof HttpError && typeof err.data === "object" && err.data
          ? (err.data as { error?: string; detail?: string }).detail ?? null
          : null;
      setError(apiDetail ?? fallback);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New data sheet"
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSubmit}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} />
            {busy ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: "grid", gap: 10 }}
      >
        <label style={{ display: "grid", gap: 4 }}>
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--portal-muted)",
              fontWeight: 600,
            }}
          >
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            placeholder="e.g. Customer PIN codes"
            maxLength={50}
            autoFocus
            style={{
              padding: "8px 12px",
              border: "1px solid var(--portal-border)",
              borderRadius: 6,
              fontSize: "0.95rem",
              fontFamily: "inherit",
              background: "#ffffff",
            }}
          />
        </label>
        {duplicate && (
          <div style={{ fontSize: "0.85rem", color: "#8a6d00" }}>
            A data sheet named "{trimmed}" already exists. It will still be
            created as a new entry.
          </div>
        )}
        {error && (
          <div style={{ fontSize: "0.85rem", color: "#c0392b" }}>{error}</div>
        )}
        {/* Hidden submit button: lets Enter from the input trigger submit
            without needing a visible button inside the form (the footer
            holds the real Create action). */}
        <button type="submit" style={{ display: "none" }} aria-hidden="true" />
      </form>
    </Modal>
  );
}

function SheetsRowList({
  items,
  onOpenRecords,
  onOpenSchema,
  canEditSchema,
}: {
  items: DataSheetSummary[];
  onOpenRecords: (item: DataSheetSummary) => void;
  onOpenSchema: (item: DataSheetSummary) => void;
  canEditSchema: boolean;
}) {
  return (
    <div className="tile-rows">
      {items.map((item) => (
        <div
          key={item.sheetId}
          className="tile tile-link tile-row"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
            padding: "12px 18px",
          }}
        >
          <button
            type="button"
            className="tile-row-title"
            onClick={() => onOpenRecords(item)}
            aria-label={`Open records for ${item.name}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              background: "transparent",
              border: 0,
              padding: 0,
              margin: 0,
              cursor: "pointer",
              textAlign: "left",
              flex: "1 1 0",
              minWidth: 0,
              color: "inherit",
              font: "inherit",
            }}
          >
            <div
              className="tile-row-icon"
              style={{ flexShrink: 0 }}
              aria-hidden="true"
            >
              <Sheet size={18} />
            </div>
            <div
              style={{
                flex: "2 1 0",
                minWidth: 0,
                fontWeight: 600,
                fontSize: "1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.name}
            >
              {item.name || "—"}
            </div>
            <div
              className="tile-row-detail"
              style={{
                flex: "2 1 0",
                minWidth: 0,
                color: "var(--portal-muted)",
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.description ?? undefined}
            >
              {item.description || ""}
            </div>
            <div style={{ flexShrink: 0 }}>
              <ColumnCountBadge count={item.columnCount} />
            </div>
          </button>
          {canEditSchema && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSchema(item);
              }}
              title="Edit schema"
              style={{ flexShrink: 0 }}
            >
              Schema
            </button>
          )}
          <button
            type="button"
            aria-label={`Open records for ${item.name}`}
            onClick={() => onOpenRecords(item)}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              margin: 0,
              cursor: "pointer",
              color: "var(--portal-primary)",
              flexShrink: 0,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

function ColumnCountBadge({ count }: { count: number }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid var(--portal-border)",
        background: "var(--portal-surface-alt)",
        color: "var(--portal-text-soft)",
        fontSize: "0.78rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {count} {count === 1 ? "column" : "columns"}
    </span>
  );
}

function LoadingState() {
  return (
    <div className="tile" style={{ color: "var(--portal-muted)" }}>
      Loading data sheets…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="tile"
      style={{
        borderColor: "#e74c3c",
        color: "#c0392b",
        background: "#fdecea",
      }}
    >
      <strong>Something went wrong.</strong>
      <div style={{ marginTop: 6, fontSize: "0.9rem" }}>{message}</div>
    </div>
  );
}

function EmptyState({
  canCreate,
  onCreate,
}: {
  canCreate: boolean;
  onCreate: () => void;
}) {
  return (
    <section
      className="tile"
      style={{
        maxWidth: 720,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 18,
        alignItems: "flex-start",
        padding: 24,
      }}
    >
      <div
        className="tile-row-icon"
        style={{ width: 56, height: 56, borderRadius: 14 }}
        aria-hidden="true"
      >
        <Sheet size={28} />
      </div>
      <div>
        <h3 style={{ marginTop: 0 }}>No data sheets yet</h3>
        <p style={{ marginTop: 0 }}>
          Data sheets give flow builder a place to look up tenant-specific
          values (PIN codes, routing tables, anything else you need as
          rows and columns).{" "}
          {canCreate ? (
            <>
              Use the <strong>+ New data sheet</strong> button above to create
              your first one.
            </>
          ) : (
            <>
              Ask an administrator to create one; they'll show up here as
              soon as they exist.
            </>
          )}
        </p>
        {canCreate && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onCreate}
            style={{
              marginTop: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Plus size={16} />
            New data sheet
          </button>
        )}
      </div>
    </section>
  );
}
